// Home = "sign-off vivo": tabela de fechamento com recorte (Seller | Indústria | BU),
// busca, ordenação e filtro componível de indústrias.
// Todo o estado vive na URL (?recorte=&q=&ord=&industrias=) — refresh e link compartilhado preservam a visão.
import { useEffect, useMemo, useState } from 'react';
import { usePodeVerDetalhe } from '../ServiceLevelContext';
import { useSearchParams } from 'react-router-dom';
import { getIndustrias, getSignoff, SignoffResponse } from '../api';
import { getPrefs, getUser } from '../auth';
import SignoffTable from '../components/SignoffTable';
import JanelaRefSelect from '../components/JanelaRefSelect';
import MultiSelect from '../components/MultiSelect';
import Dica from '../components/Dica';
import { AJUDA } from '../utils/ajudas';
import { JanelaRef, Periodo, Recorte, SignoffLinha } from '../types';
import { fmtDataHora } from '../utils/format';

const RECORTES: { id: Recorte; label: string }[] = [
  { id: 'SELLER', label: 'por Seller' },
  { id: 'INDUSTRIA', label: 'por Indústria' },
  { id: 'BU', label: 'por BU' },
];

type OrdKey = 'ns' | 'corte' | 'atraso' | 'nome';

const ORDENACOES: Record<OrdKey, { label: string; sort: (a: SignoffLinha, b: SignoffLinha) => number }> = {
  ns: { label: 'Pior NS primeiro', sort: (a, b) => (a.kpis.mtd.ns_qtd ?? 1) - (b.kpis.mtd.ns_qtd ?? 1) },
  corte: { label: 'Maior corte primeiro', sort: (a, b) => b.kpis.mtd.corte_valor - a.kpis.mtd.corte_valor },
  atraso: { label: 'Mais atrasos primeiro', sort: (a, b) => b.kpis.mtd.pedidos_em_atraso - a.kpis.mtd.pedidos_em_atraso },
  nome: { label: 'Nome (A–Z)', sort: (a, b) => a.label.localeCompare(b.label) },
};

export default function Dashboard() {
  const prefs = getPrefs();
  const user = getUser();
  const [params, setParams] = useSearchParams();

  // estado derivado da URL (fonte única): refresh/back/compartilhar preservam a visão
  const recorte = (params.get('recorte') as Recorte)
    || (user?.perfil === 'EXEC_INDUSTRIA' ? 'BU' : 'SELLER');
  const busca = params.get('q') || '';
  const ordenar = (params.get('ord') as OrdKey) || 'ns';
  const industriasSel = useMemo(
    () => (params.get('industrias') || '').split(',').map(Number).filter((n) => n > 0),
    [params]
  );

  const setParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k); else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  const [ref, setRef] = useState<JanelaRef>(prefs.janela_ref || 'YTD');
  const [custom, setCustom] = useState<Periodo>({ de: prefs.custom_de, ate: prefs.custom_ate });
  const [industrias, setIndustrias] = useState<{ id: number; nome: string }[]>([]);
  const [data, setData] = useState<SignoffResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const podeVerRecortes = usePodeVerDetalhe(2);

  useEffect(() => {
    getIndustrias().then(setIndustrias).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getSignoff(recorte, ref, custom, industriasSel).then(setData).finally(() => setLoading(false));
  }, [recorte, ref, custom.de, custom.ate, industriasSel.join(',')]);

  const linhas = useMemo(() => {
    if (!data) return [];
    const q = busca.trim().toLowerCase();
    return data.linhas
      .filter((l) => !q
        || l.label.toLowerCase().includes(q)
        || l.seller?.razao_social?.toLowerCase().includes(q)
        || l.seller?.cnpj?.includes(q))
      .sort(ORDENACOES[ordenar].sort);
  }, [data, busca, ordenar]);

  if (!data) return <p className="empty">Carregando fechamento…</p>;

  // N1: só o consolidado — recortes e busca não se aplicam
  if (!podeVerRecortes) {
    return (
      <>
        <div className="page-header">
          <div>
            <h1 className="flex items-center gap-2">Fechamento — Nível de Serviço <Dica titulo="Como ler o fechamento" texto={AJUDA.signoff} /></h1>
            <div className="sub">NS Quantidade · % no Prazo · Corte, nas 4 janelas</div>
          </div>
          <div className="filters">
            <JanelaRefSelect value={ref} custom={custom} onChange={(r, c) => { setRef(r); if (c) setCustom(c); }} />
            <span className="foto-badge">Última atualização {fmtDataHora(data.ts_foto)}</span>
          </div>
        </div>
        <div className="card">
          <SignoffTable
            total={data.total}
            linhas={[]}
            refLabel={data.total.kpis.ref_label}
            m1Label={data.total.kpis.m1_label}
          />
        </div>
        <p className="chart-note">Seu acesso mostra os números consolidados. Para ver o detalhamento por seller, indústria ou pedido, fale com o administrador.</p>
      </>
    );
  }


  const chipsIndustrias = industrias.filter((i) => industriasSel.includes(i.id));

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="flex items-center gap-2">Fechamento — Nível de Serviço <Dica titulo="Como ler o fechamento" texto={AJUDA.signoff} /></h1>
          <div className="sub">NS Quantidade · % no Prazo · Corte, nas 4 janelas</div>
        </div>
        <div className="filters">
          <JanelaRefSelect value={ref} custom={custom} onChange={(r, c) => { setRef(r); if (c) setCustom(c); }} />
          <span className="foto-badge">Última atualização {fmtDataHora(data.ts_foto)}</span>
        </div>
      </div>

      <div className="recorte-tabs">
        {RECORTES.map((r) => (
          <button key={r.id} className={recorte === r.id ? 'on' : ''} onClick={() => setParam({ recorte: r.id })}>
            {r.label}
          </button>
        ))}
      </div>

      <div className="filters mb-2">
        <input
          placeholder={recorte === 'SELLER' ? 'Buscar por nome, razão social ou CNPJ…' : 'Buscar…'}
          value={busca}
          onChange={(e) => setParam({ q: e.target.value })}
        />
        <select value={ordenar} onChange={(e) => setParam({ ord: e.target.value })}>
          {Object.entries(ORDENACOES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <MultiSelect
          rotulo="Indústrias"
          opcoes={industrias}
          selecionados={industriasSel}
          onChange={(ids) => setParam({ industrias: ids.join(',') })}
        />
        {busca && <span className="muted text-xs">{linhas.length} de {data.linhas.length}</span>}
      </div>

      {chipsIndustrias.length > 0 && (
        <div className="mb-3">
          {chipsIndustrias.map((i) => (
            <span key={i.id} className="seller-chip">
              {i.nome}
              <button onClick={() => setParam({ industrias: industriasSel.filter((s) => s !== i.id).join(',') })} title="Remover filtro">×</button>
            </span>
          ))}
          {recorte === 'SELLER' && (
            <span className="muted text-xs ml-1">KPIs dos sellers calculados só nos produtos dessas indústrias</span>
          )}
        </div>
      )}

      <div className={`card ${loading ? 'opacity-60' : ''}`}>
        <SignoffTable
          total={busca ? null : data.total}
          linhas={linhas}
          refLabel={data.total.kpis.ref_label}
          m1Label={data.total.kpis.m1_label}
        />
        {linhas.length === 0 && <p className="empty">Nada encontrado com esses filtros.</p>}
      </div>
    </>
  );
}
